import { useEffect, useRef, useState } from 'react';
import { useStore } from '../store/useStore.js';
import type { Theme } from '../store/types.js';

interface Command {
  id: string;
  label: string;
  hint?: string;
  group: string;
  action: () => void;
}

export function CommandPalette() {
  const open = useStore((s) => s.cmdPaletteOpen);
  const setCmdPaletteOpen = useStore((s) => s.setCmdPaletteOpen);
  const nodes = useStore((s) => s.nodes);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) { setQuery(''); setActiveIdx(0); setTimeout(() => inputRef.current?.focus(), 30); }
  }, [open]);

  // Scroll active item into view. (Precisa ficar antes do early-return de
  // `open` — hook depois de return condicional quebra a ordem de hooks,
  // React error #310.)
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-active="true"]`) as HTMLElement | null;
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIdx]);

  function close() { setCmdPaletteOpen(false); }

  const addNode = useStore.getState().addNode;
  const setSelected = useStore.getState().setSelected;
  const zoomToFit = useStore.getState().zoomToFit;
  const setTheme = useStore.getState().setTheme;
  const setSearch = useStore.getState().setSearch;
  const undo = useStore.getState().undo;
  const redo = useStore.getState().redo;

  function buildCommands(): Command[] {
    const staticCmds: Command[] = [
      {
        id: 'new-node',
        label: 'Criar novo nó',
        hint: 'N',
        group: 'Ações',
        action: () => {
          const cam = useStore.getState().cam;
          const id = addNode({
            label: 'Nova Ideia', type: 'idea', category: 'idea', icon: 'lightbulb',
            x: -cam.x, y: -cam.y, size: 22,
          });
          setTimeout(() => setSelected(id), 10);
        },
      },
      {
        id: 'zoom-fit',
        label: 'Centralizar grafo',
        hint: 'F',
        group: 'Navegação',
        action: () => zoomToFit(),
      },
      {
        id: 'undo',
        label: 'Desfazer',
        hint: 'Ctrl+Z',
        group: 'Histórico',
        action: () => undo(),
      },
      {
        id: 'redo',
        label: 'Refazer',
        hint: 'Ctrl+Y',
        group: 'Histórico',
        action: () => redo(),
      },
      {
        id: 'theme-dark',
        label: 'Tema: Escuro',
        group: 'Aparência',
        action: () => setTheme('dark' as Theme),
      },
      {
        id: 'theme-light',
        label: 'Tema: Claro',
        group: 'Aparência',
        action: () => setTheme('light' as Theme),
      },
      {
        id: 'theme-midnight',
        label: 'Tema: Midnight',
        group: 'Aparência',
        action: () => setTheme('midnight' as Theme),
      },
      {
        id: 'theme-forest',
        label: 'Tema: Floresta',
        group: 'Aparência',
        action: () => setTheme('forest' as Theme),
      },
      {
        id: 'clear-search',
        label: 'Limpar busca',
        group: 'Ações',
        action: () => setSearch(''),
      },
    ];

    const nodeCmds: Command[] = nodes.map((n) => ({
      id: `node-${n.id}`,
      label: `Ir para: ${n.label}`,
      hint: n.type,
      group: 'Nós',
      action: () => {
        setSelected(n.id);
        useStore.getState().setCamera({
          x: -n.x,
          y: -n.y,
          zoom: 1.2,
        });
      },
    }));

    return [...staticCmds, ...nodeCmds];
  }

  if (!open) return null;

  const allCmds = buildCommands();
  const q = query.toLowerCase().trim();
  const filtered = q
    ? allCmds.filter((c) => c.label.toLowerCase().includes(q) || c.group.toLowerCase().includes(q))
    : allCmds;

  function run(cmd: Command) { cmd.action(); close(); }

  function onKey(e: React.KeyboardEvent) {
    if (e.key === 'Escape') { close(); return; }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, filtered.length - 1));
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const cmd = filtered[activeIdx];
      if (cmd) run(cmd);
    }
  }

  // Group commands.
  const groups: Record<string, Command[]> = {};
  for (const c of filtered) {
    if (!groups[c.group]) groups[c.group] = [];
    groups[c.group]!.push(c);
  }

  let globalIdx = 0;

  return (
    <>
      <div onClick={close} style={{ position: 'fixed', inset: 0, zIndex: 590, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)' }} />
      <div
        className="glass-panel"
        style={{
          position: 'fixed',
          top: '18vh',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 540,
          maxWidth: '92vw',
          borderRadius: 14,
          zIndex: 600,
          overflow: 'hidden',
          boxShadow: '0 16px 64px rgba(0,0,0,0.7)',
        }}
        onKeyDown={onKey}
      >
        {/* Search input */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '12px 16px',
          borderBottom: '1px solid var(--border)',
        }}>
          <span style={{ color: 'var(--hint)', fontSize: 16 }}>⌘</span>
          <input
            ref={inputRef}
            type="text"
            placeholder="Buscar comandos ou nós…"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setActiveIdx(0); }}
            style={{
              flex: 1, background: 'none', border: 'none', outline: 'none',
              color: 'var(--text)', fontSize: 14, fontFamily: 'inherit',
            }}
          />
          <kbd style={kbdStyle}>Esc</kbd>
        </div>

        {/* Results */}
        <div ref={listRef} style={{ maxHeight: '60vh', overflowY: 'auto', padding: '4px' }}>
          {Object.entries(groups).map(([group, cmds]) => (
            <div key={group}>
              <div style={{ padding: '8px 12px 3px', fontSize: 10, color: 'var(--muted)', fontWeight: 700, letterSpacing: '0.08em' }}>
                {group.toUpperCase()}
              </div>
              {cmds.map((cmd) => {
                const idx = globalIdx++;
                const isActive = idx === activeIdx;
                return (
                  <button
                    key={cmd.id}
                    data-active={isActive}
                    onClick={() => run(cmd)}
                    onMouseEnter={() => setActiveIdx(idx)}
                    style={{
                      width: '100%', textAlign: 'left',
                      padding: '8px 12px',
                      border: 'none', borderRadius: 7,
                      background: isActive ? 'rgba(0,245,255,0.09)' : 'transparent',
                      color: isActive ? 'var(--neon)' : 'var(--text)',
                      fontSize: 13, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      transition: 'background 0.1s',
                    }}
                  >
                    <span>{cmd.label}</span>
                    {cmd.hint && (
                      <kbd style={{ ...kbdStyle, color: isActive ? 'var(--neon)' : 'var(--hint)' }}>
                        {cmd.hint}
                      </kbd>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
          {filtered.length === 0 && (
            <div style={{ padding: '24px', textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>
              Nenhum resultado para "{query}"
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '8px 16px',
          borderTop: '1px solid var(--border)',
          display: 'flex', gap: 16, fontSize: 11, color: 'var(--hint)',
        }}>
          <span><kbd style={kbdStyle}>↑↓</kbd> navegar</span>
          <span><kbd style={kbdStyle}>↵</kbd> executar</span>
          <span><kbd style={kbdStyle}>Esc</kbd> fechar</span>
        </div>
      </div>
    </>
  );
}

const kbdStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.07)',
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: 4,
  padding: '1px 6px',
  fontSize: 10,
  fontFamily: 'monospace',
  color: 'var(--hint)',
};
